import React, { useState, useEffect, useRef } from 'react';
import { useBorradorCampos, AvisoRescate } from './utils/borradorFormulario';
import { API_BASE } from './config/api';

const COLOR_PRINCIPAL = '#5c9adb';
const COLOR_OSCURO = '#2f5f9e';
const COLOR_VERDE = '#5cd4b0';
const COLOR_ROJO = '#e05c5c';
const COLOR_AMARILLO = '#f5d76e';
const COLOR_FONDO = '#f3f4f4';

const API_JUNTA = `${API_BASE}/api/resumir-junta`;
const API_CLASIFICAR_NOTA = `${API_BASE}/api/clasificar-nota`;

const KEY_NOTAS = 'beep_agenda_notas';
const KEY_JUNTAS = 'beep_agenda_juntas';
const KEY_PENDIENTES = 'beep_agenda_pendientes';

const ETIQUETAS = [
  { id: 'rojo',     color: '#e05c5c', label: 'Urgente' },
  { id: 'naranja',  color: '#f4a261', label: 'Servicio' },
  { id: 'azul',     color: '#5c9adb', label: 'CCTV' },
  { id: 'verde',    color: '#5cd4b0', label: 'Redes' },
  { id: 'morado',   color: '#9b59b6', label: 'Alarmas' },
  { id: 'amarillo', color: '#f5d76e', label: 'Comercial' },
  { id: 'gris',     color: '#bbb',    label: 'General' },
];

function cargarClientesBeep() {
  try { return JSON.parse(localStorage.getItem('beep_clientes') || '[]'); } catch { return []; }
}
function nombresClientesBeep() {
  return cargarClientesBeep().map(c => c.nombre);
}
function sucursalesClienteBeep(nombre) {
  const c = cargarClientesBeep().find(x => x.nombre === nombre);
  return (c?.sucursales || []).map(s => s.nombre);
}

function cargar(key, def) {
  try {
    const raw = localStorage.getItem(key);
    return raw ? JSON.parse(raw) : def;
  } catch {
    return def;
  }
}
function guardar(key, val) {
  try {
    localStorage.setItem(key, JSON.stringify(val));
  } catch {}
}

function fechaCorta(iso) {
  const d = new Date(iso);
  return `${String(d.getDate()).padStart(2, '0')}/${String(
    d.getMonth() + 1
  ).padStart(2, '0')}/${d.getFullYear()} ${String(d.getHours()).padStart(2, '0')}:${String(
    d.getMinutes()
  ).padStart(2, '0')}`;
}

function MiAgenda({ onCrearRequerimiento, onCrearTarea, onCrearProyecto, tabInicial }) {
  const [tab, setTab] = useState(tabInicial || 'notas');

  useEffect(() => {
    if (tabInicial) setTab(tabInicial);
  }, [tabInicial]);
  const [notas, setNotas] = useState(() => cargar(KEY_NOTAS, []));
  const [juntas, setJuntas] = useState(() => cargar(KEY_JUNTAS, []));
  const [pendientesActivos, setPendientesActivos] = useState(0);

  useEffect(() => guardar(KEY_NOTAS, notas), [notas]);
  useEffect(() => guardar(KEY_JUNTAS, juntas), [juntas]);

  useEffect(() => {
    const recalcular = () => {
      try {
        const raw = localStorage.getItem(KEY_PENDIENTES);
        const arr = raw ? JSON.parse(raw) : [];
        setPendientesActivos(arr.filter((p) => !p.hecha).length);
      } catch {
        setPendientesActivos(0);
      }
    };
    recalcular();
    window.addEventListener('beep:pendientes-actualizados', recalcular);
    window.addEventListener('storage', recalcular);
    return () => {
      window.removeEventListener('beep:pendientes-actualizados', recalcular);
      window.removeEventListener('storage', recalcular);
    };
  }, []);

  return (
    <div
      style={{
        background: COLOR_FONDO,
        minHeight: '100vh',
        display: 'flex',
        flexDirection: 'column',
      }}
    >
      <div
        style={{
          background: '#fff',
          padding: '14px 16px',
          borderBottom: `1px solid ${COLOR_PRINCIPAL}22`,
        }}
      >
        <div
          style={{
            fontSize: 20,
            fontWeight: 700,
            color: COLOR_OSCURO,
            marginBottom: 12,
          }}
        >
          Mi Agenda
        </div>
        <div style={{ display: 'flex', gap: 8 }}>
          <TabBtn
            activo={tab === 'notas'}
            onClick={() => setTab('notas')}
            label="📝 Notas"
          />
          <TabBtn
            activo={tab === 'juntas'}
            onClick={() => setTab('juntas')}
            label="🎙️ Juntas"
          />
          <TabBtn
            activo={tab === 'pendientes'}
            onClick={() => setTab('pendientes')}
            label="⭐ Pendientes"
            badge={pendientesActivos}
          />
        </div>
      </div>

      <div style={{ flex: 1, padding: 16, overflow: 'auto' }}>
        {tab === 'notas' && (
          <PestanaNotas
            notas={notas}
            setNotas={setNotas}
            onCrearRequerimiento={onCrearRequerimiento}
            onCrearTarea={onCrearTarea}
            onCrearProyecto={onCrearProyecto}
          />
        )}
        {tab === 'juntas' && (
          <PestanaJuntas
            juntas={juntas}
            setJuntas={setJuntas}
            onCrearRequerimiento={onCrearRequerimiento}
            onCrearTarea={onCrearTarea}
          />
        )}
        {tab === 'pendientes' && (
          <PestanaPendientes
            onCrearRequerimiento={onCrearRequerimiento}
            onCrearTarea={onCrearTarea}
            onCrearProyecto={onCrearProyecto}
          />
        )}
      </div>
    </div>
  );
}

function TabBtn({ activo, onClick, label, badge }) {
  return (
    <button
      onClick={onClick}
      style={{
        flex: 1,
        position: 'relative',
        background: activo ? COLOR_PRINCIPAL : '#fff',
        color: activo ? '#fff' : COLOR_OSCURO,
        border: `1px solid ${COLOR_PRINCIPAL}55`,
        borderRadius: 10,
        padding: '10px 8px',
        fontSize: 13,
        fontWeight: 600,
        cursor: 'pointer',
      }}
    >
      {label}
      {badge > 0 && (
        <span
          style={{
            position: 'absolute',
            top: -6,
            right: -6,
            background: COLOR_ROJO,
            color: '#fff',
            borderRadius: 10,
            fontSize: 11,
            padding: '2px 6px',
            border: '2px solid #fff',
          }}
        >
          {badge}
        </span>
      )}
    </button>
  );
}

/* ============ NOTAS ============ */
function PestanaNotas({ notas, setNotas, onCrearRequerimiento, onCrearTarea, onCrearProyecto }) {
  const [busqueda, setBusqueda] = useState('');
  const [orden, setOrden] = useState('reciente');
  const [editando, setEditando] = useState(null); // nota o {nueva:true}
  const [verArchivadas, setVerArchivadas] = useState(false);

  const archivadas = notas.filter((n) => n.archivada);
  const visibles = notas
    .filter((n) => !n.archivada)
    .filter((n) => {
      if (!busqueda) return true;
      const t = busqueda.toLowerCase();
      return (
        (n.titulo || '').toLowerCase().includes(t) ||
        (n.contenido || '').toLowerCase().includes(t)
      );
    })
    .sort((a, b) => {
      if (orden === 'reciente') return b.fecha.localeCompare(a.fecha);
      if (orden === 'antigua') return a.fecha.localeCompare(b.fecha);
      if (orden === 'etiqueta') return (a.etiqueta || '').localeCompare(b.etiqueta || '');
      return 0;
    });

  const abrirNueva = () =>
    setEditando({
      id: Date.now(),
      titulo: '',
      contenido: '',
      etiqueta: 'azul',
      fecha: new Date().toISOString(),
      nueva: true,
    });

  const guardarNota = (nota) => {
    if (nota.nueva) {
      setNotas([{ ...nota, nueva: false }, ...notas]);
    } else {
      setNotas(notas.map((n) => (n.id === nota.id ? nota : n)));
    }
    setEditando(null);
  };

  const archivar = (id) => {
    setNotas(notas.map((n) => (n.id === id ? { ...n, archivada: true } : n)));
    setEditando(null);
  };

  return (
    <div>
      <div style={{ display: 'flex', gap: 8, marginBottom: 12 }}>
        <input
          type="text"
          placeholder="🔍 Buscar..."
          value={busqueda}
          onChange={(e) => setBusqueda(e.target.value)}
          style={inputBase}
        />
        <select
          value={orden}
          onChange={(e) => setOrden(e.target.value)}
          style={{ ...inputBase, maxWidth: 140 }}
        >
          <option value="reciente">Más reciente</option>
          <option value="antigua">Más antigua</option>
          <option value="etiqueta">Por etiqueta</option>
        </select>
      </div>

      <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
        {visibles.length === 0 && (
          <div style={vacio}>No tienes notas. Toca + para crear una.</div>
        )}
        {visibles.map((n) => {
          const color =
            ETIQUETAS.find((e) => e.id === n.etiqueta)?.color || COLOR_PRINCIPAL;
          return (
            <button
              key={n.id}
              onClick={() => setEditando(n)}
              style={{
                textAlign: 'left',
                background: '#fff',
                border: 'none',
                borderLeft: `4px solid ${color}`,
                borderRadius: 10,
                padding: 12,
                cursor: 'pointer',
                boxShadow: '0 1px 3px rgba(0,0,0,0.06)',
              }}
            >
              {n.titulo && (
                <div style={{ fontWeight: 600, color: COLOR_OSCURO, marginBottom: 4 }}>
                  {n.titulo}
                </div>
              )}
              <div
                style={{
                  fontSize: 13,
                  color: '#444',
                  whiteSpace: 'pre-wrap',
                  display: '-webkit-box',
                  WebkitLineClamp: 3,
                  WebkitBoxOrient: 'vertical',
                  overflow: 'hidden',
                }}
              >
                {n.contenido}
              </div>
              {n.clienteVinculado && (
                <div style={{ display: 'flex', gap: 4, flexWrap: 'wrap', marginTop: 6 }}>
                  <span style={{
                    fontSize: 10, fontWeight: 700,
                    background: COLOR_PRINCIPAL + '22', color: COLOR_OSCURO,
                    padding: '2px 8px', borderRadius: 10,
                  }}>
                    🏢 {n.clienteVinculado.nombre}
                  </span>
                  {n.clienteVinculado.sucursal && (
                    <span style={{
                      fontSize: 10, fontWeight: 600,
                      background: '#eef0f3', color: '#666',
                      padding: '2px 8px', borderRadius: 10,
                    }}>
                      {n.clienteVinculado.sucursal}
                    </span>
                  )}
                </div>
              )}
              <div style={{ fontSize: 11, color: '#888', marginTop: 6 }}>
                {fechaCorta(n.fecha)}
              </div>
            </button>
          );
        })}
      </div>

      {/* ARCHIVO */}
      {archivadas.length > 0 && (
        <div style={{ marginTop: 16 }}>
          <button
            onClick={() => setVerArchivadas(!verArchivadas)}
            style={{
              width: '100%', textAlign: 'left',
              background: '#fff', border: `1px solid ${COLOR_PRINCIPAL}33`,
              borderRadius: 10, padding: 12, cursor: 'pointer',
              display: 'flex', justifyContent: 'space-between', alignItems: 'center',
              fontFamily: 'inherit',
            }}
          >
            <span style={{ fontWeight: 700, color: COLOR_OSCURO, fontSize: 13 }}>
              📁 Notas archivadas ({archivadas.length})
            </span>
            <span style={{ color: COLOR_OSCURO }}>{verArchivadas ? '▲' : '▼'}</span>
          </button>
          {verArchivadas && (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 8, marginTop: 8 }}>
              {archivadas.map((n) => {
                const color = ETIQUETAS.find((e) => e.id === n.etiqueta)?.color || COLOR_PRINCIPAL;
                return (
                  <div key={n.id} style={{
                    background: '#f9fafb', borderLeft: `4px solid ${color}`,
                    borderRadius: 10, padding: 12, opacity: 0.85,
                  }}>
                    {n.titulo && (
                      <div style={{ fontWeight: 600, color: COLOR_OSCURO, marginBottom: 4, fontSize: 13 }}>
                        {n.titulo}
                      </div>
                    )}
                    <div style={{ fontSize: 12, color: '#666', whiteSpace: 'pre-wrap' }}>
                      {(n.contenido || '').slice(0, 120)}{(n.contenido || '').length > 120 ? '...' : ''}
                    </div>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: 6 }}>
                      <span style={{ fontSize: 11, color: '#888' }}>{fechaCorta(n.fecha)}</span>
                      <button
                        onClick={() => setNotas(notas.map((x) => x.id === n.id ? { ...x, archivada: false } : x))}
                        style={{
                          background: 'transparent', border: `1px solid ${COLOR_PRINCIPAL}55`,
                          color: COLOR_OSCURO, borderRadius: 8, padding: '4px 10px',
                          fontSize: 11, fontWeight: 600, cursor: 'pointer', fontFamily: 'inherit',
                        }}
                      >
                        ↩ Desarchivar
                      </button>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      )}

      <button
        onClick={abrirNueva}
        aria-label="Nueva nota"
        style={fab}
      >
        +
      </button>

      {editando && (
        <ModalNota
          nota={editando}
          onGuardar={guardarNota}
          onCerrar={() => setEditando(null)}
          onArchivar={() => archivar(editando.id)}
          onConvertirRequerimiento={() => {
            if (onCrearRequerimiento) {
              onCrearRequerimiento({
                descripcion: editando.contenido,
                titulo: editando.titulo,
              });
              setEditando(null);
              alert('✓ Nota convertida en requerimiento');
            }
          }}
          onConvertirTarea={() => {
            if (onCrearTarea) {
              onCrearTarea({
                texto: editando.titulo || editando.contenido,
              });
              setEditando(null);
              alert('✓ Nota convertida en tarea');
            }
          }}
          onConvertirProyecto={() => {
            if (onCrearProyecto) {
              onCrearProyecto({
                nombre: editando.titulo || editando.contenido,
                descripcion: editando.contenido,
              });
              setEditando(null);
              alert('✓ Nota convertida en proyecto');
            }
          }}
        />
      )}
    </div>
  );
}

function ModalNota({
  nota,
  onGuardar,
  onCerrar,
  onArchivar,
  onConvertirRequerimiento,
  onConvertirTarea,
  onConvertirProyecto,
}) {
  const [titulo, setTitulo] = useState(nota.titulo || '');
  const [contenido, setContenido] = useState(nota.contenido || '');
  const [etiqueta, setEtiqueta] = useState(nota.etiqueta || 'gris');
  const [resumenIA, setResumenIA] = useState(
    nota.resumenIA
      ? (typeof nota.resumenIA === 'string' ? { resumen: [nota.resumenIA], pendientes: [] } : nota.resumenIA)
      : null
  );
  const [cargandoIA, setCargandoIA] = useState(false);
  const [sugerenciaIA, setSugerenciaIA] = useState(null); // {color, razon}
  const [tieneCliente, setTieneCliente] = useState(!!nota.clienteVinculado);
  const [cliente, setCliente] = useState(nota.clienteVinculado?.nombre || '');
  const [busquedaCliente, setBusquedaCliente] = useState('');
  const [sucursal, setSucursal] = useState(nota.clienteVinculado?.sucursal || '');
  const [equipo, setEquipo] = useState(nota.clienteVinculado?.equipo || '');

  // Borrador local (TANDA 2): misma mecanica que Editar expediente, clave usuario:pantalla:registro.
  // Los valores INICIALES son los que trae la nota, para no avisar de "cambios" cuando no los hay.
  const inicialNota = {
    titulo: nota.titulo || '', contenido: nota.contenido || '', etiqueta: nota.etiqueta || 'gris',
    tieneCliente: !!nota.clienteVinculado, cliente: nota.clienteVinculado?.nombre || '',
    sucursal: nota.clienteVinculado?.sucursal || '', equipo: nota.clienteVinculado?.equipo || '',
  };
  const camposBorrador = { titulo, contenido, etiqueta, tieneCliente, cliente, sucursal, equipo };
  const aplicarBorrador = (d) => {
    if (d.titulo != null) setTitulo(d.titulo);
    if (d.contenido != null) setContenido(d.contenido);
    if (d.etiqueta != null) setEtiqueta(d.etiqueta);
    if (d.tieneCliente != null) setTieneCliente(!!d.tieneCliente);
    if (d.cliente != null) setCliente(d.cliente);
    if (d.sucursal != null) setSucursal(d.sucursal);
    if (d.equipo != null) setEquipo(d.equipo);
  };
  // La nota NUEVA se abre con un id de reloj (`Date.now()`), distinto en cada apertura: si se usara
  // ese id como clave, el borrador quedaria huerfano y el rescate no aparecerra NUNCA. Clave fija.
  const bor = useBorradorCampos('agenda-nota', nota.nueva ? 'nueva' : nota.id, camposBorrador, aplicarBorrador, { iniciales: inicialNota });

  const sucursalesDisp = cliente ? sucursalesClienteBeep(cliente) : [];
  const clientesFiltrados = busquedaCliente
    ? nombresClientesBeep().filter((c) => c.toLowerCase().includes(busquedaCliente.toLowerCase()))
    : [];

  // Clasificación automática por IA con debounce
  useEffect(() => {
    if (!contenido || contenido.trim().length < 15) {
      setSugerenciaIA(null);
      return;
    }
    const timer = setTimeout(async () => {
      try {
        const res = await fetch(API_CLASIFICAR_NOTA, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ contenido }),
        });
        const data = await res.json();
        if (data && data.color && ETIQUETAS.find((e) => e.id === data.color)) {
          setSugerenciaIA(data);
        }
      } catch {}
    }, 2000);
    return () => clearTimeout(timer);
  }, [contenido]);

  const procesarIA = async () => {
    if (cargandoIA) return;
    setCargandoIA(true);
    try {
      const res = await fetch(API_JUNTA, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ transcripcion: contenido }),
      });
      const data = await res.json();
      setResumenIA(data);
    } catch {
      alert('Error al procesar con IA');
    } finally {
      setCargandoIA(false);
    }
  };

  return (
    <div style={modalOverlay}>
      <div style={modalCard}>
        <AvisoRescate bor={bor} />
        <input
          type="text"
          placeholder="Título (opcional)"
          value={titulo}
          onChange={(e) => setTitulo(e.target.value)}
          style={{ ...inputBase, fontWeight: 600, fontSize: 16 }}
        />

        {/* TOGGLE CLIENTE */}
        <label style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 12, color: '#444', cursor: 'pointer' }}>
          <input
            type="checkbox"
            checked={tieneCliente}
            onChange={(e) => {
              setTieneCliente(e.target.checked);
              if (!e.target.checked) {
                setCliente(''); setSucursal(''); setEquipo(''); setBusquedaCliente('');
              }
            }}
          />
          ¿Esta nota es sobre un cliente?
        </label>

        {tieneCliente && (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 8, padding: 10, background: '#eaf2fb', borderRadius: 10 }}>
            <div style={{ position: 'relative' }}>
              <input
                type="text"
                placeholder="Buscar cliente..."
                value={cliente || busquedaCliente}
                onChange={(e) => {
                  setCliente('');
                  setSucursal('');
                  setBusquedaCliente(e.target.value);
                }}
                style={{ ...inputBase, width: '100%' }}
              />
              {clientesFiltrados.length > 0 && !cliente && (
                <div style={{
                  position: 'absolute', top: '100%', left: 0, right: 0,
                  background: '#fff', border: `1px solid ${COLOR_PRINCIPAL}55`,
                  borderRadius: 8, marginTop: 2, zIndex: 5,
                  boxShadow: '0 4px 12px rgba(0,0,0,0.1)',
                }}>
                  {clientesFiltrados.map((c) => (
                    <div
                      key={c}
                      onClick={() => { setCliente(c); setBusquedaCliente(''); setSucursal(''); }}
                      style={{ padding: '8px 12px', fontSize: 13, cursor: 'pointer', borderBottom: '1px solid #f0f0f0' }}
                    >
                      {c}
                    </div>
                  ))}
                </div>
              )}
            </div>
            {cliente && sucursalesDisp.length > 0 && (
              <select value={sucursal} onChange={(e) => setSucursal(e.target.value)} style={{ ...inputBase, width: '100%' }}>
                <option value="">Selecciona sucursal...</option>
                {sucursalesDisp.map((s) => <option key={s} value={s}>{s}</option>)}
              </select>
            )}
            {cliente && (
              <input
                type="text"
                placeholder="Equipo o área (ej: Cámara entrada principal)"
                value={equipo}
                onChange={(e) => setEquipo(e.target.value)}
                style={{ ...inputBase, width: '100%' }}
              />
            )}
          </div>
        )}

        <textarea
          placeholder="Escribe tu nota..."
          value={contenido}
          onChange={(e) => setContenido(e.target.value)}
          rows={8}
          style={{ ...inputBase, resize: 'vertical', fontFamily: 'inherit' }}
        />
        <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
          <span style={{ fontSize: 12, color: '#666' }}>Etiqueta:</span>
          {ETIQUETAS.map((e) => (
            <button
              key={e.id}
              onClick={() => setEtiqueta(e.id)}
              title={e.label}
              style={{
                width: 24,
                height: 24,
                borderRadius: '50%',
                background: e.color,
                border: etiqueta === e.id ? '3px solid #222' : '2px solid #fff',
                cursor: 'pointer',
              }}
            />
          ))}
        </div>

        {sugerenciaIA && sugerenciaIA.color !== etiqueta && (
          <div style={{
            display: 'flex', alignItems: 'center', gap: 8,
            background: '#fffbe6', border: '1px dashed #d4b94e',
            borderRadius: 10, padding: '8px 12px', fontSize: 12, color: '#5a4a00',
          }}>
            <span style={{
              width: 14, height: 14, borderRadius: '50%',
              background: ETIQUETAS.find((e) => e.id === sugerenciaIA.color)?.color || '#bbb',
              flexShrink: 0,
            }} />
            <span style={{ flex: 1 }}>
              💡 IA sugiere: <strong>{ETIQUETAS.find((e) => e.id === sugerenciaIA.color)?.label || sugerenciaIA.color}</strong>
              {sugerenciaIA.razon && <span style={{ color: '#888' }}> — {sugerenciaIA.razon}</span>}
            </span>
            <button
              onClick={() => { setEtiqueta(sugerenciaIA.color); setSugerenciaIA(null); }}
              style={{
                background: COLOR_PRINCIPAL, color: '#fff', border: 'none',
                borderRadius: 8, padding: '4px 10px', fontSize: 11, fontWeight: 700,
                cursor: 'pointer', fontFamily: 'inherit',
              }}
            >
              Aplicar
            </button>
          </div>
        )}

        {contenido.length > 50 && (
          <button
            type="button"
            onClick={procesarIA}
            disabled={cargandoIA}
            style={{
              ...btnSecundario,
              background: COLOR_PRINCIPAL,
              color: '#fff',
              border: 'none',
            }}
          >
            {cargandoIA ? 'Procesando...' : '✨ IA — Resumir esta nota'}
          </button>
        )}

        {resumenIA && (
          <div
            style={{
              background: '#eaf2fb',
              border: `1.5px solid ${COLOR_PRINCIPAL}`,
              borderRadius: 10,
              padding: 12,
              fontSize: 13,
              color: '#222',
            }}
          >
            <div style={{ fontWeight: 700, color: COLOR_OSCURO, marginBottom: 6 }}>
              ✨ Resumen IA
            </div>
            {(resumenIA.resumen || []).map((p, i) => (
              <div key={i} style={{ marginBottom: 4 }}>• {p}</div>
            ))}
            {(resumenIA.pendientes || []).length > 0 && (
              <div style={{ marginTop: 8 }}>
                <strong>Pendientes:</strong>
                {resumenIA.pendientes.map((p, i) => (
                  <div key={i}>· {p.texto}</div>
                ))}
              </div>
            )}
          </div>
        )}

        {!nota.nueva && (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
            <button onClick={onConvertirRequerimiento} style={btnSecundario}>
              ➕ Convertir en requerimiento
            </button>
            <button onClick={onConvertirTarea} style={btnSecundario}>
              ✅ Convertir en tarea
            </button>
            <button onClick={onConvertirProyecto} style={btnSecundario}>
              📁 Convertir en proyecto
            </button>
            <button
              onClick={onArchivar}
              style={{ ...btnSecundario, color: COLOR_ROJO }}
            >
              🗄️ Archivar
            </button>
          </div>
        )}

        <div style={{ display: 'flex', gap: 8, marginTop: 4 }}>
          <button onClick={bor.confirmarCierre(onCerrar)} style={{ ...btnGris, flex: 1 }}>
            Cancelar
          </button>
          <button
            onClick={() => {
              bor.limpiar(); // guardada: el borrador ya no hace falta
              onGuardar({
                ...nota,
                titulo,
                contenido,
                etiqueta,
                clienteVinculado: tieneCliente && cliente
                  ? { nombre: cliente, sucursal, equipo }
                  : null,
                resumenIA: resumenIA
                  ? (resumenIA.resumen || []).join(' • ')
                  : (nota.resumenIA || ''),
                fecha: nota.nueva ? nota.fecha : new Date().toISOString(),
              });
            }}
            style={{ ...btnPrimario, flex: 1 }}
          >
            Guardar
          </button>
        </div>
      </div>
    </div>
  );
}

/* ============ JUNTAS ============ */

async function activarWakeLock() {
  try {
    if ('wakeLock' in navigator) {
      return await navigator.wakeLock.request('screen');
    }
  } catch (err) {
    console.log('Wake Lock no disponible:', err);
  }
  return null;
}

function PestanaJuntas({ juntas, setJuntas, onCrearRequerimiento, onCrearTarea }) {
  const [tituloJunta, setTituloJunta] = useState('');
  const [tema, setTema] = useState('');
  const [cliente, setCliente] = useState('');
  const [busquedaCliente, setBusquedaCliente] = useState('');
  const [sucursal, setSucursal] = useState('');
  const [grabando, setGrabando] = useState(false);
  const [transcripcion, setTranscripcion] = useState('');
  const [procesando, setProcesando] = useState(false);
  const [resultado, setResultado] = useState(null); // {resumen,pendientes,requerimientos,decisiones}
  const [seleccion, setSeleccion] = useState({});
  const [tiempo, setTiempo] = useState(0); // segundos
  const recRef = useRef(null);
  const wakeLockRef = useRef(null);
  const inicioRef = useRef(null);
  const timerRef = useRef(null);

  const sucursalesDisponibles = cliente ? sucursalesClienteBeep(cliente) : [];
  const clientesFiltrados = busquedaCliente
    ? nombresClientesBeep().filter((c) => c.toLowerCase().includes(busquedaCliente.toLowerCase()))
    : [];

  const liberarWakeLock = () => {
    if (wakeLockRef.current) {
      try { wakeLockRef.current.release(); } catch {}
      wakeLockRef.current = null;
    }
  };

  useEffect(() => {
    return () => {
      liberarWakeLock();
      if (timerRef.current) clearInterval(timerRef.current);
      if (recRef.current) {
        try { recRef.current.stop(); } catch {}
      }
    };
  }, []);

  const formatoTiempo = (s) => {
    const m = Math.floor(s / 60);
    const sec = s % 60;
    return `${String(m).padStart(2, '0')}:${String(sec).padStart(2, '0')}`;
  };

  const iniciar = async () => {
    const SR = window.SpeechRecognition || window.webkitSpeechRecognition;
    if (!SR) {
      alert('Tu navegador no soporta reconocimiento de voz.');
      return;
    }
    const rec = new SR();
    rec.lang = 'es-MX';
    rec.continuous = true;
    rec.interimResults = true;
    rec.onresult = (e) => {
      let txt = '';
      for (let i = 0; i < e.results.length; i++) {
        txt += e.results[i][0].transcript + ' ';
      }
      setTranscripcion(txt);
    };
    rec.onend = () => setGrabando(false);
    rec.onerror = () => setGrabando(false);
    recRef.current = rec;
    setTranscripcion('');
    setResultado(null);
    setGrabando(true);
    setTiempo(0);
    inicioRef.current = Date.now();
    timerRef.current = setInterval(() => {
      setTiempo(Math.floor((Date.now() - inicioRef.current) / 1000));
    }, 1000);
    wakeLockRef.current = await activarWakeLock();
    rec.start();
  };

  const finalizar = async () => {
    if (recRef.current) {
      try { recRef.current.stop(); } catch {}
    }
    if (timerRef.current) {
      clearInterval(timerRef.current);
      timerRef.current = null;
    }
    liberarWakeLock();
    setGrabando(false);
    const duracion = inicioRef.current ? Math.floor((Date.now() - inicioRef.current) / 1000) : tiempo;
    if (!transcripcion.trim()) return;
    setProcesando(true);
    try {
      const res = await fetch(API_JUNTA, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ transcripcion }),
      });
      const data = await res.json();
      setResultado(data);
      const nueva = {
        id: Date.now(),
        titulo: tituloJunta || 'Junta sin título',
        tema,
        cliente,
        sucursal,
        transcripcion,
        resumen: data.resumen || [],
        pendientes: data.pendientes || [],
        requerimientos: data.requerimientos || [],
        decisiones: data.decisiones || [],
        fecha: new Date().toISOString(),
        duracion,
      };
      setJuntas([nueva, ...juntas]);
    } catch {
      alert('Error al procesar la junta');
    } finally {
      setProcesando(false);
    }
  };

  const toggleSel = (key) => setSeleccion((s) => ({ ...s, [key]: !s[key] }));

  const crearSeleccionados = () => {
    if (!resultado) return;
    (resultado.pendientes || []).forEach((p, i) => {
      if (seleccion[`p${i}`] && onCrearTarea) onCrearTarea({ texto: p.texto });
    });
    (resultado.requerimientos || []).forEach((r, i) => {
      if (seleccion[`r${i}`] && onCrearRequerimiento)
        onCrearRequerimiento({
          descripcion: r.descripcion,
          tipo: r.tipo,
          urgencia: r.urgencia,
        });
    });
    setSeleccion({});
    alert('Seleccionados creados.');
  };

  return (
    <div>
      {!grabando && !resultado && !procesando && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 10, marginBottom: 12 }}>
          <input
            type="text"
            placeholder="Ej: Visita Bodegas Alianza"
            value={tituloJunta}
            onChange={(e) => setTituloJunta(e.target.value)}
            style={{ ...inputBase, width: '100%' }}
          />
          <input
            type="text"
            placeholder="Ej: Renovación sistema CCTV"
            value={tema}
            onChange={(e) => setTema(e.target.value)}
            style={{ ...inputBase, width: '100%' }}
          />
          <div style={{ position: 'relative' }}>
            <input
              type="text"
              placeholder="Buscar cliente..."
              value={cliente || busquedaCliente}
              onChange={(e) => {
                setCliente('');
                setSucursal('');
                setBusquedaCliente(e.target.value);
              }}
              style={{ ...inputBase, width: '100%' }}
            />
            {clientesFiltrados.length > 0 && !cliente && (
              <div style={{
                position: 'absolute', top: '100%', left: 0, right: 0,
                background: '#fff', border: `1px solid ${COLOR_PRINCIPAL}55`,
                borderRadius: 8, marginTop: 2, zIndex: 5,
                boxShadow: '0 4px 12px rgba(0,0,0,0.08)',
              }}>
                {clientesFiltrados.map((c) => (
                  <div
                    key={c}
                    onClick={() => {
                      setCliente(c);
                      setBusquedaCliente('');
                      setSucursal('');
                    }}
                    style={{ padding: '8px 12px', fontSize: 13, cursor: 'pointer', borderBottom: '1px solid #f0f0f0' }}
                  >
                    {c}
                  </div>
                ))}
              </div>
            )}
          </div>
          {cliente && sucursalesDisponibles.length > 0 && (
            <select
              value={sucursal}
              onChange={(e) => setSucursal(e.target.value)}
              style={{ ...inputBase, width: '100%' }}
            >
              <option value="">Selecciona sucursal...</option>
              {sucursalesDisponibles.map((s) => (
                <option key={s} value={s}>{s}</option>
              ))}
            </select>
          )}
        </div>
      )}
      {!grabando && !resultado && !procesando && (
        <button
          onClick={iniciar}
          disabled={!tituloJunta.trim()}
          style={{
            ...btnPrimario,
            width: '100%',
            padding: '20px',
            fontSize: 16,
            opacity: tituloJunta.trim() ? 1 : 0.5,
            cursor: tituloJunta.trim() ? 'pointer' : 'not-allowed',
          }}
        >
          🎙️ Iniciar escucha de junta
        </button>
      )}

      {grabando && (
        <div>
          <style>{`@keyframes beepPulse { 0%,100% { opacity: 1 } 50% { opacity: 0.55 } }`}</style>
          <div
            style={{
              background: COLOR_ROJO,
              color: '#fff',
              padding: '10px 14px',
              borderRadius: 10,
              fontWeight: 700,
              fontSize: 13,
              marginBottom: 12,
              textAlign: 'center',
              animation: 'beepPulse 1.4s ease-in-out infinite',
            }}
          >
            🔴 Grabando junta — pantalla activa
          </div>
          <div
            style={{
              background: '#fff',
              borderRadius: 12,
              padding: 16,
              border: `2px solid ${COLOR_ROJO}`,
            }}
          >
            <div style={{
              display: 'flex', justifyContent: 'space-between', alignItems: 'center',
              marginBottom: 10,
            }}>
              <div style={{ color: COLOR_ROJO, fontWeight: 700, fontSize: 13 }}>
                {tituloJunta}
              </div>
              <div style={{
                fontFamily: 'monospace', fontSize: 22, fontWeight: 700, color: COLOR_OSCURO,
              }}>
                {formatoTiempo(tiempo)}
              </div>
            </div>
            <div style={{ fontSize: 13, color: '#222', whiteSpace: 'pre-wrap', minHeight: 80 }}>
              {transcripcion || 'Escuchando...'}
            </div>
            <button
              onClick={finalizar}
              style={{
                marginTop: 12,
                width: '100%',
                background: COLOR_ROJO,
                color: '#fff',
                border: 'none',
                borderRadius: 10,
                padding: '18px',
                fontSize: 16,
                fontWeight: 700,
                cursor: 'pointer',
              }}
            >
              ⏹ Finalizar junta
            </button>
          </div>
        </div>
      )}

      {procesando && <div style={vacio}>Procesando junta con IA...</div>}

      {resultado && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
          <CardSeccion titulo="📋 Resumen ejecutivo">
            {(resultado.resumen || []).map((p, i) => (
              <div key={i} style={lineaItem}>• {p}</div>
            ))}
          </CardSeccion>

          <CardSeccion titulo="⭐ Pendientes detectados">
            {(resultado.pendientes || []).map((p, i) => (
              <label key={i} style={lineaCheck}>
                <input
                  type="checkbox"
                  checked={!!seleccion[`p${i}`]}
                  onChange={() => toggleSel(`p${i}`)}
                />
                <span>{p.texto}{p.responsable ? ` — ${p.responsable}` : ''}</span>
              </label>
            ))}
          </CardSeccion>

          <CardSeccion titulo="📌 Requerimientos detectados">
            {(resultado.requerimientos || []).map((r, i) => (
              <label key={i} style={lineaCheck}>
                <input
                  type="checkbox"
                  checked={!!seleccion[`r${i}`]}
                  onChange={() => toggleSel(`r${i}`)}
                />
                <span>{r.descripcion} <em style={{ color: '#888' }}>({r.tipo}/{r.urgencia})</em></span>
              </label>
            ))}
          </CardSeccion>

          <CardSeccion titulo="✅ Decisiones tomadas">
            {(resultado.decisiones || []).map((d, i) => (
              <div key={i} style={lineaItem}>• {d}</div>
            ))}
          </CardSeccion>

          <button onClick={crearSeleccionados} style={btnPrimario}>
            Crear seleccionados
          </button>
          <button
            onClick={() => {
              setResultado(null);
              setTranscripcion('');
            }}
            style={btnGris}
          >
            Nueva junta
          </button>
        </div>
      )}

      {!grabando && !resultado && juntas.length > 0 && (
        <div style={{ marginTop: 24 }}>
          <div style={{ fontWeight: 600, color: COLOR_OSCURO, marginBottom: 8 }}>
            Historial de juntas
          </div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
            {juntas.map((j) => (
              <button
                key={j.id}
                onClick={() => setResultado(j)}
                style={{
                  textAlign: 'left',
                  background: '#fff',
                  border: `1px solid ${COLOR_PRINCIPAL}33`,
                  borderRadius: 10,
                  padding: 12,
                  cursor: 'pointer',
                }}
              >
                <div style={{ fontWeight: 600, color: COLOR_OSCURO, fontSize: 13 }}>
                  {j.titulo || 'Junta sin título'}
                </div>
                <div style={{ fontSize: 11, color: '#888' }}>{fechaCorta(j.fecha)}</div>
                <div style={{ fontSize: 13, color: '#222', marginTop: 4 }}>
                  {(j.resumen && j.resumen[0]) || 'Junta sin resumen'}
                </div>
              </button>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

function CardSeccion({ titulo, children }) {
  return (
    <div
      style={{
        background: '#fff',
        borderRadius: 12,
        padding: 12,
        border: `1px solid ${COLOR_PRINCIPAL}33`,
      }}
    >
      <div style={{ fontWeight: 600, color: COLOR_OSCURO, marginBottom: 8 }}>{titulo}</div>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>{children}</div>
    </div>
  );
}

/* ============ PENDIENTES ============ */
function PestanaPendientes({
  onCrearRequerimiento,
  onCrearTarea,
  onCrearProyecto,
}) {
  const [pendientes, setPendientes] = useState([]);
  const [inputTexto, setInputTexto] = useState('');
  const [grabando, setGrabando] = useState(false);
  const [menuId, setMenuId] = useState(null);
  const recRef = useRef(null);

  // Cargar de localStorage al montar
  useEffect(() => {
    try {
      const raw = localStorage.getItem('beep_agenda_pendientes');
      if (raw) setPendientes(JSON.parse(raw));
    } catch {}
  }, []);

  // Guardar en localStorage cuando cambia
  useEffect(() => {
    try {
      localStorage.setItem('beep_agenda_pendientes', JSON.stringify(pendientes));
      window.dispatchEvent(new Event('beep:pendientes-actualizados'));
    } catch {}
  }, [pendientes]);

  const agregarPendiente = () => {
    const v = inputTexto.trim();
    if (!v) return;
    setPendientes((prev) => [
      {
        id: Date.now(),
        texto: v,
        hecha: false,
        prioridad: 'media',
        fechaLimite: null,
        creado: new Date().toISOString(),
      },
      ...prev,
    ]);
    setInputTexto('');
  };

  const agregarDictado = (t) => {
    const v = (t || '').trim();
    if (!v) return;
    setPendientes((prev) => [
      {
        id: Date.now(),
        texto: v,
        hecha: false,
        prioridad: 'media',
        fechaLimite: null,
        creado: new Date().toISOString(),
      },
      ...prev,
    ]);
  };

  const toggle = (id) =>
    setPendientes((prev) =>
      prev.map((p) => (p.id === id ? { ...p, hecha: !p.hecha } : p))
    );

  const setPrioridad = (id, prio) =>
    setPendientes((prev) =>
      prev.map((p) => (p.id === id ? { ...p, prioridad: prio } : p))
    );

  const setFecha = (id, fecha) =>
    setPendientes((prev) =>
      prev.map((p) => (p.id === id ? { ...p, fechaLimite: fecha } : p))
    );

  const eliminar = (id) =>
    setPendientes((prev) => prev.filter((p) => p.id !== id));

  const dictar = () => {
    const SR = window.SpeechRecognition || window.webkitSpeechRecognition;
    if (!SR) {
      alert('Tu navegador no soporta reconocimiento de voz.');
      return;
    }
    if (grabando && recRef.current) {
      recRef.current.stop();
      return;
    }
    const rec = new SR();
    rec.lang = 'es-MX';
    rec.interimResults = false;
    rec.continuous = false;
    rec.onresult = (e) => agregarDictado(e.results[0][0].transcript);
    rec.onend = () => setGrabando(false);
    rec.onerror = () => setGrabando(false);
    recRef.current = rec;
    setGrabando(true);
    rec.start();
  };

  const ordenadas = [
    ...pendientes.filter((p) => !p.hecha),
    ...pendientes.filter((p) => p.hecha),
  ];

  const colorPrio = (p) =>
    p === 'alta' ? COLOR_ROJO : p === 'baja' ? COLOR_VERDE : COLOR_AMARILLO;

  return (
    <div>
      <div style={{ display: 'flex', gap: 8, marginBottom: 12 }}>
        <input
          type="text"
          placeholder="Nuevo pendiente..."
          value={inputTexto}
          onChange={(e) => setInputTexto(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Enter') {
              e.preventDefault();
              agregarPendiente();
            }
          }}
          style={inputBase}
        />
        <button
          type="button"
          onClick={dictar}
          style={{
            ...btnPrimario,
            background: grabando ? COLOR_ROJO : COLOR_PRINCIPAL,
            width: 44,
          }}
        >
          🎤
        </button>
        <button type="button" onClick={agregarPendiente} style={{ ...btnPrimario, width: 44 }}>
          +
        </button>
      </div>

      <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
        {ordenadas.length === 0 && <div style={vacio}>No tienes pendientes.</div>}
        {ordenadas.map((p) => (
          <div
            key={p.id}
            style={{
              background: '#fff',
              borderLeft: `4px solid ${colorPrio(p.prioridad)}`,
              borderRadius: 10,
              padding: 10,
              display: 'flex',
              flexDirection: 'column',
              gap: 6,
            }}
          >
            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              <input
                type="checkbox"
                checked={p.hecha}
                onChange={() => toggle(p.id)}
                style={{ accentColor: COLOR_VERDE }}
              />
              <span
                style={{
                  flex: 1,
                  fontSize: 14,
                  color: p.hecha ? '#999' : '#222',
                  textDecoration: p.hecha ? 'line-through' : 'none',
                }}
              >
                {p.texto}
              </span>
              <button
                onClick={() => setMenuId(menuId === p.id ? null : p.id)}
                style={{
                  background: 'transparent',
                  border: `1px solid ${COLOR_PRINCIPAL}55`,
                  borderRadius: 8,
                  padding: '4px 10px',
                  fontSize: 12,
                  color: COLOR_OSCURO,
                  cursor: 'pointer',
                }}
              >
                → Convertir
              </button>
              <button
                onClick={() => eliminar(p.id)}
                style={{ background: 'transparent', border: 'none', color: '#bbb', cursor: 'pointer' }}
              >
                ✕
              </button>
            </div>

            <div style={{ display: 'flex', gap: 6, alignItems: 'center', flexWrap: 'wrap' }}>
              <select
                value={p.prioridad}
                onChange={(e) => setPrioridad(p.id, e.target.value)}
                style={{
                  fontSize: 11,
                  border: `1px solid ${colorPrio(p.prioridad)}`,
                  borderRadius: 6,
                  padding: '2px 6px',
                  color: colorPrio(p.prioridad),
                  fontWeight: 600,
                }}
              >
                <option value="alta">Alta</option>
                <option value="media">Media</option>
                <option value="baja">Baja</option>
              </select>
              <input
                type="date"
                value={p.fechaLimite || ''}
                onChange={(e) => setFecha(p.id, e.target.value)}
                style={{
                  fontSize: 11,
                  border: `1px solid ${COLOR_PRINCIPAL}55`,
                  borderRadius: 6,
                  padding: '2px 6px',
                }}
              />
            </div>

            {menuId === p.id && (
              <div style={{ display: 'flex', gap: 6, marginTop: 4 }}>
                <button
                  onClick={() => {
                    if (onCrearRequerimiento)
                      onCrearRequerimiento({ descripcion: p.texto });
                    setMenuId(null);
                  }}
                  style={btnMini}
                >
                  Requerimiento
                </button>
                <button
                  onClick={() => {
                    if (onCrearTarea) onCrearTarea({ texto: p.texto });
                    setMenuId(null);
                  }}
                  style={btnMini}
                >
                  Tarea
                </button>
                <button
                  onClick={() => {
                    if (onCrearProyecto) onCrearProyecto({ nombre: p.texto });
                    setMenuId(null);
                  }}
                  style={btnMini}
                >
                  Proyecto
                </button>
              </div>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}

/* ============ ESTILOS ============ */
const inputBase = {
  flex: 1,
  minWidth: 0,
  border: `1px solid ${COLOR_PRINCIPAL}55`,
  borderRadius: 8,
  padding: '10px 12px',
  fontSize: 14,
  outline: 'none',
  background: '#fff',
};

const btnPrimario = {
  background: COLOR_OSCURO,
  color: '#fff',
  border: 'none',
  borderRadius: 8,
  padding: '10px 14px',
  fontSize: 14,
  fontWeight: 600,
  cursor: 'pointer',
};

const btnGris = {
  background: '#e6e8eb',
  color: '#333',
  border: 'none',
  borderRadius: 8,
  padding: '10px 14px',
  fontSize: 14,
  fontWeight: 600,
  cursor: 'pointer',
};

const btnSecundario = {
  background: '#fff',
  color: COLOR_OSCURO,
  border: `1px solid ${COLOR_PRINCIPAL}77`,
  borderRadius: 8,
  padding: '8px 12px',
  fontSize: 13,
  fontWeight: 600,
  cursor: 'pointer',
  textAlign: 'left',
};

const btnMini = {
  flex: 1,
  background: COLOR_PRINCIPAL,
  color: '#fff',
  border: 'none',
  borderRadius: 6,
  padding: '6px 8px',
  fontSize: 11,
  fontWeight: 600,
  cursor: 'pointer',
};

const fab = {
  position: 'fixed',
  bottom: 80,
  left: 24,
  right: 'auto',
  width: 56,
  height: 56,
  borderRadius: '50%',
  background: COLOR_OSCURO,
  color: '#fff',
  border: 'none',
  fontSize: 28,
  cursor: 'pointer',
  boxShadow: '0 4px 16px rgba(47,95,158,0.35)',
  zIndex: 900,
};

const vacio = {
  textAlign: 'center',
  padding: 24,
  color: '#888',
  fontSize: 13,
};

const lineaItem = { fontSize: 13, color: '#222' };
const lineaCheck = {
  display: 'flex',
  gap: 8,
  alignItems: 'flex-start',
  fontSize: 13,
  color: '#222',
  cursor: 'pointer',
};

const modalOverlay = {
  position: 'fixed',
  inset: 0,
  background: 'rgba(0,0,0,0.5)',
  zIndex: 1200,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  padding: 16,
};

const modalCard = {
  background: '#fff',
  borderRadius: 14,
  padding: 16,
  width: '100%',
  maxWidth: 480,
  maxHeight: 'calc(var(--beep-vh) * 90)',
  overflowY: 'auto',
  display: 'flex',
  flexDirection: 'column',
  // TANDA-1: sin minHeight:0 el contenido estira la caja y lo de abajo queda fuera de alcance.
  minHeight: 0,
  gap: 10,
};

export default MiAgenda;
